// Pure compute helpers for the Analysis page.
//
// Everything in here takes the raw hook outputs (weight entries, meals,
// sessions, profile, etc.) and a range descriptor, and returns shapes
// that the analysis sections can render directly. No React, no i18n —
// callers handle the wording.
//
// Date strings are ISO yyyy-mm-dd throughout, matching what the hooks
// return. We never construct Date objects from local timestamps to
// avoid TZ drift around midnight.

import { estimateStrengthKcal } from './cardioCalories'

const MS_PER_DAY = 24 * 60 * 60 * 1000

function todayIso() {
  return new Date().toLocaleDateString('en-CA')
}

// Calories burned in a single session = stored cardio block kcal
// (already estimated at log time) + a MET-based estimate for the
// strength portion when there are exercise logs and a duration. Uses
// the session's own body_weight_kg when set, falling back to the
// user's profile start weight for older sessions that don't have one.
export function sessionTotalKcal(session, profile) {
  if (!session) return 0
  const cardio = (session.cardio_logs || []).reduce(
    (s, c) => s + (Number(c.calories_estimated) || 0),
    0,
  )
  let strength = 0
  if ((session.exercise_logs || []).length > 0) {
    const weight =
      Number(session.body_weight_kg) ||
      Number(profile?.start_weight) ||
      70
    const kcal = estimateStrengthKcal({
      durationMin: session.duration_min,
      bodyWeightKg: weight,
    })
    if (kcal != null) strength = kcal
  }
  return cardio + strength
}

function isoFromOffset(daysBack) {
  const d = new Date(Date.now() - daysBack * MS_PER_DAY)
  return d.toLocaleDateString('en-CA')
}

// Resolve a range descriptor to a [startIso, endIso] window (inclusive).
// 'phase' falls back to a 30-day window when no phase is active.
export function resolveRange(range, currentPhase) {
  const end = todayIso()
  if (range === 'phase' && currentPhase?.start_date) {
    return {
      start: currentPhase.start_date,
      end: currentPhase.end_date && currentPhase.end_date < end ? currentPhase.end_date : end,
    }
  }
  const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 30
  return { start: isoFromOffset(days - 1), end }
}

export function isInRange(date, { start, end }) {
  return date >= start && date <= end
}

// Sum kcal/macros across the meals for a single date. Missing values
// count as zero — we only consider a day's data "missing" if there are
// zero meal entries for it.
function sumDayMacros(meals) {
  return meals.reduce(
    (acc, m) => ({
      kcal: acc.kcal + (Number(m.kcal) || 0),
      protein: acc.protein + (Number(m.protein_g) || 0),
      carbs: acc.carbs + (Number(m.carbs_g) || 0),
      fat: acc.fat + (Number(m.fat_g) || 0),
    }),
    { kcal: 0, protein: 0, carbs: 0, fat: 0 }
  )
}

// Group meal entries by date string. Returns a Map<isoDate, totals>.
export function macrosByDate(mealEntries) {
  const byDate = new Map()
  for (const m of mealEntries) {
    if (!byDate.has(m.date)) byDate.set(m.date, [])
    byDate.get(m.date).push(m)
  }
  const result = new Map()
  for (const [date, list] of byDate) {
    result.set(date, sumDayMacros(list))
  }
  return result
}

// Latest scale reading per date — handles multiple entries per day by
// keeping the most recent one (entries arrive newest-first from the hook).
export function weightByDate(weightEntries) {
  const result = new Map()
  for (const e of weightEntries) {
    if (!result.has(e.date)) result.set(e.date, Number(e.weight))
  }
  return result
}

// Iterate the [start, end] window day-by-day, returning the ISO strings.
function eachDay(range) {
  const out = []
  let d = new Date(`${range.start}T00:00:00`)
  const end = new Date(`${range.end}T00:00:00`)
  while (d <= end) {
    out.push(d.toLocaleDateString('en-CA'))
    d = new Date(d.getTime() + MS_PER_DAY)
  }
  return out
}

// Per-date map of total kcal burned across all sessions on that day.
// Multiple sessions on the same date sum together.
export function kcalBurnedByDate(sessions, profile) {
  const out = new Map()
  for (const s of sessions || []) {
    if (!s.date) continue
    const kcal = sessionTotalKcal(s, profile)
    out.set(s.date, (out.get(s.date) || 0) + kcal)
  }
  return out
}

// Day-by-day series for the Weight × Calories overlay chart. Each entry
// has a date, the weight reading (or null if not weighed that day),
// total kcal consumed (or null if no meals logged that day), and total
// kcal burned (or null if no sessions on that day).
export function weightCaloriesSeries({ weightEntries, mealEntries, sessions, profile, range }) {
  const w = weightByDate(weightEntries)
  const m = macrosByDate(mealEntries)
  const b = kcalBurnedByDate(sessions, profile)
  return eachDay(range).map((date) => ({
    date,
    weight: w.has(date) ? w.get(date) : null,
    kcal: m.has(date) ? m.get(date).kcal : null,
    kcalBurned: b.has(date) ? b.get(date) : null,
  }))
}

// Compute the weight-change rate over the range, expressed per week.
// Uses (last - first) / weeks rather than a linear fit — simpler and
// matches how people read scale data.
export function weeklyWeightChange({ weightEntries, range }) {
  const inRange = weightEntries.filter((e) => isInRange(e.date, range))
  if (inRange.length < 2) return null
  // Hook order is newest-first; sort ascending to take first/last cleanly.
  const sorted = [...inRange].sort((a, b) => a.date.localeCompare(b.date))
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  const days =
    (new Date(`${last.date}T00:00:00`) - new Date(`${first.date}T00:00:00`)) / MS_PER_DAY
  if (days <= 0) return null
  const totalDelta = Number(last.weight) - Number(first.weight)
  return (totalDelta / days) * 7
}

// % of in-range days that hit a given target (within tolerance). For
// macros we use a symmetric ±10% band; for kcal we use the same band so
// the metric is comparable across the four bars in the UI.
const ADHERENCE_TOLERANCE = 0.1

function withinTolerance(actual, target, tolerance = ADHERENCE_TOLERANCE) {
  if (target == null || target <= 0) return null
  if (actual == null) return false
  return actual >= target * (1 - tolerance) && actual <= target * (1 + tolerance)
}

export function macroAdherence({ mealEntries, profile, range }) {
  const byDate = macrosByDate(mealEntries)
  const days = eachDay(range)
  const daysWithMeals = days.filter((d) => byDate.has(d))

  const targets = {
    kcal: profile?.kcal_target,
    protein: profile?.protein_target_g,
    carbs: profile?.carbs_target_g,
    fat: profile?.fat_target_g,
  }
  const hasAnyTarget =
    targets.kcal != null || targets.protein != null ||
    targets.carbs != null || targets.fat != null
  if (!hasAnyTarget) {
    return { hasAnyTarget: false, daysCounted: 0, kcal: null, protein: null, carbs: null, fat: null }
  }

  // Hits where the target is set; null where it isn't. Adherence % is
  // (hits / total counted days). Days without any meal data are counted
  // as misses — otherwise a user who didn't log skews the percentage.
  let kcalHits = 0
  let proteinHits = 0
  let carbsHits = 0
  let fatHits = 0
  for (const d of daysWithMeals) {
    const totals = byDate.get(d)
    if (withinTolerance(totals.kcal, targets.kcal)) kcalHits += 1
    if (withinTolerance(totals.protein, targets.protein)) proteinHits += 1
    if (withinTolerance(totals.carbs, targets.carbs)) carbsHits += 1
    if (withinTolerance(totals.fat, targets.fat)) fatHits += 1
  }
  const totalDays = days.length
  return {
    hasAnyTarget: true,
    daysCounted: totalDays,
    daysWithMeals: daysWithMeals.length,
    kcal: targets.kcal != null ? kcalHits / totalDays : null,
    protein: targets.protein != null ? proteinHits / totalDays : null,
    carbs: targets.carbs != null ? carbsHits / totalDays : null,
    fat: targets.fat != null ? fatHits / totalDays : null,
  }
}

// Did this date have at least one session logged? Used to classify
// training vs rest days for the matrix and for training-aware insights.
export function trainingDays(sessions) {
  const set = new Set()
  for (const s of sessions || []) {
    if (s.date) set.add(s.date)
  }
  return set
}

export function trainingRestStats({ mealEntries, sessions, weightEntries, profile, range }) {
  const trainingSet = trainingDays(sessions)
  const macroDates = macrosByDate(mealEntries)
  const weightDates = weightByDate(weightEntries)
  const burnedDates = kcalBurnedByDate(sessions, profile)
  const days = eachDay(range)

  const buckets = {
    training: { kcal: [], protein: [], carbs: [], fat: [], burned: [], weights: [] },
    rest: { kcal: [], protein: [], carbs: [], fat: [], burned: [], weights: [] },
  }
  for (const d of days) {
    const slot = trainingSet.has(d) ? 'training' : 'rest'
    const totals = macroDates.get(d)
    if (totals) {
      buckets[slot].kcal.push(totals.kcal)
      buckets[slot].protein.push(totals.protein)
      buckets[slot].carbs.push(totals.carbs)
      buckets[slot].fat.push(totals.fat)
    }
    // kcal burned counts even when the user didn't log meals — a
    // training-day burn is real regardless of meal-logging adherence.
    if (burnedDates.has(d)) buckets[slot].burned.push(burnedDates.get(d))
    if (weightDates.has(d)) buckets[slot].weights.push(weightDates.get(d))
  }
  const avg = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
  return {
    training: {
      days: buckets.training.kcal.length,
      kcal: avg(buckets.training.kcal),
      protein: avg(buckets.training.protein),
      carbs: avg(buckets.training.carbs),
      fat: avg(buckets.training.fat),
      kcalBurned: avg(buckets.training.burned),
    },
    rest: {
      days: buckets.rest.kcal.length,
      kcal: avg(buckets.rest.kcal),
      protein: avg(buckets.rest.protein),
      carbs: avg(buckets.rest.carbs),
      fat: avg(buckets.rest.fat),
      kcalBurned: avg(buckets.rest.burned),
    },
  }
}

// Count consecutive days with at least one log, walking back from today.
// `dateSet` is a Set of ISO date strings.
//
// If today doesn't have an entry yet, we start counting from yesterday
// — otherwise the streak resets to 0 every morning until the user
// logs, which is too punishing. The streak only truly breaks if both
// today AND yesterday are missing.
function currentStreakFromSet(dateSet, fromDate = todayIso()) {
  let d = new Date(`${fromDate}T00:00:00`)
  if (!dateSet.has(d.toLocaleDateString('en-CA'))) {
    d = new Date(d.getTime() - MS_PER_DAY)
  }
  let streak = 0
  while (true) {
    const iso = d.toLocaleDateString('en-CA')
    if (!dateSet.has(iso)) break
    streak += 1
    d = new Date(d.getTime() - MS_PER_DAY)
  }
  return streak
}

// Longest run of consecutive days in `dateSet`. Naive scan — fine for
// hundreds of dates.
function bestStreakFromSet(dateSet) {
  const sorted = Array.from(dateSet).sort()
  let best = 0
  let run = 0
  let prev = null
  for (const iso of sorted) {
    if (prev) {
      const diff = (new Date(`${iso}T00:00:00`) - new Date(`${prev}T00:00:00`)) / MS_PER_DAY
      run = diff === 1 ? run + 1 : 1
    } else {
      run = 1
    }
    if (run > best) best = run
    prev = iso
  }
  return best
}

export function loggingStreaks({ weightEntries, mealEntries, waterEntries }) {
  const weightSet = new Set(weightEntries.map((e) => e.date))
  const mealSet = new Set(mealEntries.map((m) => m.date))
  const waterSet = new Set((waterEntries || []).map((e) => e.date))
  return {
    weight: {
      current: currentStreakFromSet(weightSet),
      best: bestStreakFromSet(weightSet),
    },
    meals: {
      current: currentStreakFromSet(mealSet),
      best: bestStreakFromSet(mealSet),
    },
    water: {
      current: currentStreakFromSet(waterSet),
      best: bestStreakFromSet(waterSet),
    },
  }
}

// Generate a curated set of insight cards. Each insight is { id, severity,
// tParams } and the UI maps id → translation key for the actual copy.
// Severity drives the accent color (positive / negative / neutral).
// Average kcal burned per training day in range. Skips days with zero
// sessions so the average reflects actual training output.
function avgTrainingDayBurn(sessions, profile, range) {
  if (!sessions?.length) return null
  const byDate = kcalBurnedByDate(sessions, profile)
  const days = eachDay(range).filter((d) => byDate.has(d))
  if (days.length === 0) return null
  const total = days.reduce((s, d) => s + (byDate.get(d) || 0), 0)
  return total / days.length
}

export function generateInsights({
  weightEntries,
  mealEntries,
  sessions,
  profile,
  range,
  weeklyChange,
  adherence,
  trainingVsRest,
}) {
  const insights = []

  // Weekly weight change. We split into two insight IDs (losing/gaining)
  // rather than passing the direction word as a param — that way each
  // language can phrase the sentence naturally instead of having an
  // untranslated English word inserted mid-sentence.
  if (weeklyChange != null && Math.abs(weeklyChange) > 0.05) {
    insights.push({
      id: weeklyChange < 0 ? 'weeklyChangeLosing' : 'weeklyChangeGaining',
      severity: 'neutral',
      params: {
        rate: Math.abs(weeklyChange).toFixed(2),
      },
    })
  }

  // Average daily kcal in range vs target.
  if (profile?.kcal_target) {
    const macroByDate = macrosByDate(mealEntries)
    const days = eachDay(range).filter((d) => macroByDate.has(d))
    if (days.length > 0) {
      const total = days.reduce((sum, d) => sum + macroByDate.get(d).kcal, 0)
      const avgKcal = Math.round(total / days.length)
      const delta = avgKcal - profile.kcal_target
      insights.push({
        id: 'kcalAvg',
        severity: 'neutral',
        params: {
          avg: avgKcal,
          target: profile.kcal_target,
          delta: delta > 0 ? `+${delta}` : String(delta),
        },
      })
    }
  }

  // Training vs rest day calorie delta. Only shown when there's data
  // on both sides — otherwise the comparison is meaningless.
  if (
    trainingVsRest?.training?.kcal != null &&
    trainingVsRest?.rest?.kcal != null &&
    trainingVsRest.training.days >= 2 &&
    trainingVsRest.rest.days >= 2
  ) {
    const delta = Math.round(trainingVsRest.training.kcal - trainingVsRest.rest.kcal)
    if (Math.abs(delta) >= 50) {
      insights.push({
        id: 'trainingRestKcal',
        severity: 'neutral',
        params: {
          delta: delta > 0 ? `+${delta}` : String(delta),
        },
      })
    }
  }

  // Average kcal burned per training day. Useful framing in a cut —
  // pairs with the kcalAvg insight above so the user can read both
  // sides of the energy balance.
  const burnAvg = avgTrainingDayBurn(sessions, profile, range)
  if (burnAvg != null && burnAvg >= 50) {
    insights.push({
      id: 'kcalBurnedAvg',
      severity: 'neutral',
      params: { burn: Math.round(burnAvg) },
    })
  }

  // Best logging streak across all three domains, surfaced as
  // motivation. Uses the meal log since that's the hardest one to keep.
  const mealSet = new Set(mealEntries.map((m) => m.date))
  const mealBest = bestStreakFromSet(mealSet)
  if (mealBest >= 3) {
    insights.push({
      id: 'mealStreak',
      severity: 'positive',
      params: { streak: mealBest },
    })
  }

  // Protein adherence callout — most actionable macro to flag when low.
  if (adherence?.protein != null && adherence.daysCounted > 0) {
    const pct = Math.round(adherence.protein * 100)
    if (pct < 50) {
      insights.push({
        id: 'lowProtein',
        severity: 'negative',
        params: { pct },
      })
    } else if (pct >= 80) {
      insights.push({
        id: 'highProtein',
        severity: 'positive',
        params: { pct },
      })
    }
  }

  return insights
}
